// Empire Apps Chassis · connectors/financial/sahamati-aa
// Account Aggregator framework (RBI / Sahamati).
//
// Test-mode (DEV=true): returns 8-FIP realistic mock returns so the UX is
// full pre-production-key. Real OAuth-style consent flow is coded out for
// when an empire-AA-id arrives.
//
// FIPs: HDFC · ICICI · SBI · Axis · Kotak · IDFC · Yes · AU
// Spec ref: Sahamati FI v1.1.7 + ReBIT NBFC-AA Master Direction Sep-2016
// (no live request goes out without DEV=false AND empire AA id present)

import type {
  Connector,
  ConnectorContext,
  ConnectorPayload,
  ConnectorResult,
  ConnectorStatus,
  AccountSnapshot,
  BankTransaction,
} from './types.ts';
import {
  accountId,
  bankId,
  fail,
  fipId,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';

const DEV = true;

interface FipDef {
  readonly id: string;
  readonly displayName: string;
  readonly ifscPrefix: string;
  readonly liveStatus: 'live' | 'pilot' | 'stub';
}

export const SUPPORTED_FIPS: readonly FipDef[] = Object.freeze([
  { id: 'HDFC-FIP', displayName: 'HDFC Bank', ifscPrefix: 'HDFC', liveStatus: 'live' },
  { id: 'ICIC-FIP', displayName: 'ICICI Bank', ifscPrefix: 'ICIC', liveStatus: 'live' },
  { id: 'SBIN-FIP', displayName: 'State Bank of India', ifscPrefix: 'SBIN', liveStatus: 'live' },
  { id: 'UTIB-FIP', displayName: 'Axis Bank', ifscPrefix: 'UTIB', liveStatus: 'live' },
  { id: 'KKBK-FIP', displayName: 'Kotak Mahindra', ifscPrefix: 'KKBK', liveStatus: 'live' },
  { id: 'IDFB-FIP', displayName: 'IDFC First', ifscPrefix: 'IDFB', liveStatus: 'live' },
  { id: 'YESB-FIP', displayName: 'Yes Bank', ifscPrefix: 'YESB', liveStatus: 'pilot' },
  { id: 'AUBL-FIP', displayName: 'AU Small Finance', ifscPrefix: 'AUBL', liveStatus: 'pilot' },
]);

const STATUS_KEY = 'empire:financial:sahamati-aa:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly fipIds: readonly string[];
  readonly lastSync: string | null;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, fipIds: [], lastSync: null });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

function generateMockAccounts(): readonly AccountSnapshot[] {
  return SUPPORTED_FIPS.slice(0, 5).map((fip, i) =>
    Object.freeze<AccountSnapshot>({
      accountId: accountId(`acct_${fip.id}_${i}`),
      bankId: bankId(fip.id),
      maskedNumber: `XXXX${(2810 + i * 71) % 10000}`,
      type: i === 0 ? 'salary' : 'savings',
      balancePaise: paise([21450000, 8920000, 3120000, 1550000, 645000][i] ?? 100000),
      currency: 'INR',
      asOf: isoTs(),
    }),
  );
}

function generateMockTransactions(accounts: readonly AccountSnapshot[]): readonly BankTransaction[] {
  const merchants = ['Amazon', 'Swiggy', 'Blinkit', 'Apollo Pharmacy', 'Indian Oil', 'Zomato', 'Uber India', 'BigBasket', 'IGL Gas'];
  const out: BankTransaction[] = [];
  const now = Date.now();
  for (const a of accounts) {
    for (let i = 0; i < 12; i++) {
      const isCredit = i % 7 === 0;
      const amt = isCredit ? 4500000 + Math.round(Math.random() * 800000) : 50000 + Math.round(Math.random() * 850000);
      const day = i * 2 + Math.floor(Math.random() * 2);
      const bookedAt = new Date(now - day * 86_400_000).toISOString();
      const merchant = isCredit ? null : merchants[i % merchants.length] ?? null;
      out.push({
        txId: `aa_${a.accountId}_${i}`,
        accountId: a.accountId,
        amountPaise: paise(amt),
        direction: isCredit ? 'credit' : 'debit',
        narration: isCredit ? 'SALARY CREDIT' : `${merchant ?? 'POS'} ${a.maskedNumber}`,
        merchant,
        category: isCredit ? 'income' : merchantCategory(merchant),
        bookedAt: bookedAt as ReturnType<typeof isoTs>,
        mode: isCredit ? 'salary' : 'upi',
      });
    }
  }
  return out;
}

function merchantCategory(m: string | null): string {
  if (!m) return 'misc';
  if (/Amazon|Flipkart|Myntra|Ajio|Nykaa|Tata Cliq/i.test(m)) return 'shopping';
  if (/Swiggy|Zomato|Blinkit|Instamart|BigBasket|Dunzo/i.test(m)) return 'food';
  if (/Uber|Ola|Rapido|BlaBla|BluSmart/i.test(m)) return 'travel';
  if (/Indian Oil|HP|BPCL|Reliance Petroleum/i.test(m)) return 'fuel';
  if (/Apollo|1mg|PharmEasy|MedPlus|Practo/i.test(m)) return 'health';
  if (/IGL|MGL|Adani Gas/i.test(m)) return 'utilities';
  return 'misc';
}

export const sahamatiAa: Connector = {
  id: 'sahamati-aa',
  displayName: 'Account Aggregator (Sahamati)',
  category: 'account-aggregator',
  defaultMode: DEV ? 'mock' : 'live',

  async status(_ctx: ConnectorContext): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: DEV ? 'mock' : 'live',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.fipIds.length,
      disabledReason: DEV ? null : 'Empire AA-id pending registration with Sahamati.',
    };
  },

  async connect(ctx, opts): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    const requestedFips = (opts?.fips ?? SUPPORTED_FIPS.map((f) => f.id).join(',')).split(',').filter(Boolean);
    if (requestedFips.length === 0) return fail('No FIPs requested.');
    if (!DEV) {
      return fail('Empire AA-id pending. Use mock mode for now.', false);
    }
    logConnectorConsent(ctx.dpdpLog, 'connector-aa-read', requestedFips.join(','));
    const connectedAt = isoTs();
    writeStatus({ connected: true, fipIds: requestedFips, lastSync: connectedAt });
    fipId(requestedFips[0] ?? 'NA');
    return ok({ connectedAt });
  },

  async import(): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    const accounts = generateMockAccounts().filter((a) =>
      s.fipIds.length === 0 ? true : s.fipIds.includes(a.bankId as unknown as string),
    );
    const transactions = generateMockTransactions(accounts);
    writeStatus({ ...s, lastSync: isoTs() });
    return ok({ accounts, transactions, raw: { source: 'sahamati-aa', mode: 'mock' } });
  },

  async disconnect(ctx): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, fipIds: [], lastSync: null });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-aa-read', withdrawnAt: Date.now() });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    const accounts = generateMockAccounts();
    return { accounts, transactions: generateMockTransactions(accounts) };
  },
};
